import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MIcon } from '../@material-extend';
import GenericModal from './GenericModal';

/**
 * A customizable confirmation modal component with various interaction options.
 * It supports optional header icons, custom action buttons, and a configurable layout.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.cancelText='Annuler'] - Text for the cancel button.
 * @param {React.Node} props.children - Child components to render inside the modal.
 * @param {string} [props.confirmText='Valider'] - Text for the confirm button.
 * @param {string} props.criticalActionText - Text for the danger action button.
 * @param {boolean} props.hideHeaderIcon - Whether to hide the icon in the header of the modal.
 * @param {string} props.message - A message to display in the modal.
 * @param {boolean} props.open - Controls the visibility of the modal.
 * @param {React.Node} props.picture - An optional image or element to display in the modal.
 * @param {string} props.secondaryActionText - Text for the secondary action button.
 * @param {string} props.title - The title of the modal.
 * @param {string} props.variant - The variant of the modal which determines the icon ('danger', 'warning', 'info').
 * @param {Function} props.handleCancel - Function to call when the cancel button is clicked.
 * @param {Function} props.handleClose - Function to call to close the modal.
 * @param {Function} props.handleConfirm - Function to call when the confirm button is clicked.
 * @param {Function} props.handleSecondaryAction - Function to call when the secondary action button is clicked.
 * @param {Function} props.handleCriticalAction - Function to call when the danger action button is clicked.
 * @returns {JSX.Element} A modal dialog component.
 */
const ConfirmationModal = (props) => {
  const {
    cancelText = 'Annuler',
    children,
    confirmText = 'Valider',
    criticalActionText,
    hideHeaderIcon = false,
    message,
    open = false,
    picture = null,
    secondaryActionText,
    title,
    variant,
    handleCancel,
    handleConfirm,
    handleClose,
    handleCriticalAction,
    handleSecondaryAction,
    isConfirmationLabel,
    ...other
  } = props;

  const { palette } = useTheme();
  const [loading, setLoading] = useState(false);

  const showConfirm = !!handleConfirm;
  const showSecondary = !!handleSecondaryAction && !!secondaryActionText;
  const showCritical = !!handleCriticalAction && !!criticalActionText;
  const showCancel = !!handleCancel;

  let icon = '';
  if (!hideHeaderIcon) {
    switch (variant) {
      case 'critical':
        icon = 'error';
        break;
      case 'warning':
        icon = 'warning';
        break;
      case 'info':
        icon = 'info';
        break;
      default:
    }
  }

  useEffect(() => {
    if (!open) {
      setLoading(false);
    }
  }, [open]);

  // Libération du loading après l'await ET via le useEffect sur `open` :
  // ça couvre à la fois (a) les consumers qui ferment la modale après l'op (l'effect agit),
  // (b) ceux qui gardent la modale ouverte sur erreur pour permettre un retry — sans ce
  // try/finally, le bouton restait bloqué en loading si la modale ne se fermait pas.
  const handleCriticalLocal = async (e) => {
    setLoading(true);
    try {
      await handleCriticalAction(e);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLocal = async (e) => {
    setLoading(true);
    try {
      await handleConfirm(e);
    } finally {
      setLoading(false);
    }
  };

  const modalActions = (handleCancel || handleSecondaryAction || handleConfirm) && (
    <>
      {showConfirm && (
        <Button
          id="button-validation"
          variant="contained"
          type="submit"
          loading={isConfirmationLabel ? false : loading}
          onClick={handleConfirmLocal}
        >
          {confirmText}
        </Button>
      )}

      {showCritical && (
        <Button id="button-critical" variant="contained" color="error" loading={loading} onClick={handleCriticalLocal}>
          {criticalActionText}
        </Button>
      )}

      {showSecondary && (
        <Button id="button-secondary" variant="outlined" onClick={handleSecondaryAction}>
          {secondaryActionText}
        </Button>
      )}

      {showCancel && (
        <Button
          id="button-cancel"
          variant={!showConfirm && !showSecondary && !showCritical ? 'contained' : 'text'}
          sx={{ color: showCritical ? palette.text.primary : null }}
          onClick={handleCancel}
        >
          {cancelText}
        </Button>
      )}
    </>
  );

  return (
    <GenericModal
      title={title}
      actions={modalActions}
      icon={!hideHeaderIcon ? <MIcon icon={icon} variant={variant} filled /> : null}
      maxWidth="sm"
      open={open}
      handleClose={handleClose}
      {...other}
    >
      {picture && (
        <Stack alignItems="center" p={2}>
          {picture}
        </Stack>
      )}
      {message && (
        <Typography variant="body1" sx={{ my: 5 }}>
          {message}
        </Typography>
      )}

      {children}
    </GenericModal>
  );
};

ConfirmationModal.propTypes = {
  cancelText: PropTypes.string,
  children: PropTypes.node,
  confirmText: PropTypes.string,
  criticalActionText: PropTypes.string,
  hideHeaderIcon: PropTypes.bool,
  message: PropTypes.string,
  open: PropTypes.bool,
  picture: PropTypes.node,
  title: PropTypes.string,
  secondaryActionText: PropTypes.string,
  sx: PropTypes.object,
  variant: PropTypes.string,
  handleCancel: PropTypes.func,
  handleClose: PropTypes.func,
  handleConfirm: PropTypes.func,
  handleSecondaryAction: PropTypes.func,
  handleCriticalAction: PropTypes.func,
  isConfirmationLabel: PropTypes.string
};

export default ConfirmationModal;
